// src/components/intake/IntakeQuestionScreen.js
//
// Ticket 061 (Advisor Intake I4). Renders ONE Section 1 screen from
// intakeConfig.js. Nothing in Section 1 can be skipped: Next stays disabled
// until the screen is answered. All answers are written through the callbacks;
// nothing is stored here.

import React from 'react'
import IntakeOptionButton from './IntakeOptionButton'
import IntakeFollowUp from './IntakeFollowUp'
import IntakeCountField from './IntakeCountField'
import IntakeMoveDate from './IntakeMoveDate'
import IntakeFilingHistory from './IntakeFilingHistory'
import {
  SECTION1_SCREENS,
  COMUNIDAD_OPTIONS,
  CITIZENSHIP_STATUS_OPTIONS,
  ASSET_SPLIT_OPTIONS,
  AGE_BAND_OPTIONS,
  MARITAL_CHOICES,
  FORAL_COMUNIDAD_OPTIONS,
  FORAL_NOTICE,
  FOLLOW_UP_TEXT,
  EU_FOLLOW_UP_OPTIONS,
  COUNT_ROWS,
  DISABILITY_LABEL,
} from '../../lib/intake/intakeConfig'
import { applicableKeys, known, countsProblem } from '../../lib/intake/section1Facts'

const YES_NO = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
]

export default function IntakeQuestionScreen({
  screenId, answers, taxYear, yearOptions, today,
  onMove, onFact, onClearFact, onTaxYear, onFiling, onNext,
  canNext, nextLabel, notice, busy,
}) {
  const cfg = SECTION1_SCREENS[screenId]
  const facts = answers.facts
  const applies = applicableKeys(facts)
  const set = (key) => (fact) => onFact(key, fact)

  function renderBody() {
    switch (screenId) {
      case '1.2':
        return <IntakeMoveDate move={answers.move} onMove={onMove} today={today} />

      case '1.1':
        return (
          <div className="u-stack-sm">
            {yearOptions.length === 0 && (
              <p className="intake-notice">
                None of the tax years we can look at yet start after the date you gave. Go back to change your move date.
              </p>
            )}
            {yearOptions.map((year) => (
              <IntakeOptionButton key={year} label={String(year)} selected={taxYear === year} onClick={() => onTaxYear(year)} />
            ))}
          </div>
        )

      case '1.3': {
        const fact = facts.comunidad_autonoma
        const foral = fact && fact.status === 'known_value'
          ? FORAL_COMUNIDAD_OPTIONS.find((o) => o.value === fact.value)
          : null
        return (
          <div className="u-stack-sm">
            <select
              className="cal-select"
              aria-label={cfg.title}
              value={fact && fact.status === 'known_value' ? fact.value : ''}
              onChange={(e) => (e.target.value ? onFact('comunidad_autonoma', known(e.target.value)) : onClearFact('comunidad_autonoma'))}
            >
              <option value="">Select…</option>
              {COMUNIDAD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {foral && <p className="intake-notice">{FORAL_NOTICE(foral.label)}</p>}
          </div>
        )
      }

      case '1.4':
        return (
          <div className="u-stack-sm">
            <IntakeFollowUp options={CITIZENSHIP_STATUS_OPTIONS} fact={facts.citizenship_status} onPick={set('citizenship_status')} />
            {applies.eu_citizen && (
              <IntakeFollowUp
                sub
                question={FOLLOW_UP_TEXT.eu_citizen.question}
                hint={FOLLOW_UP_TEXT.eu_citizen.hint}
                options={EU_FOLLOW_UP_OPTIONS}
                fact={facts.eu_citizen}
                onPick={set('eu_citizen')}
                notSure
              />
            )}
          </div>
        )

      case '1.5':
        return (
          <div className="u-stack-sm">
            <IntakeFollowUp options={MARITAL_CHOICES} fact={facts.marital_status} onPick={set('marital_status')} />
            {applies.asset_split && (
              <IntakeFollowUp
                sub
                question={FOLLOW_UP_TEXT.asset_split.question}
                hint={FOLLOW_UP_TEXT.asset_split.hint}
                options={ASSET_SPLIT_OPTIONS}
                fact={facts.asset_split}
                onPick={set('asset_split')}
                notSure
              />
            )}
            {applies.spouse_citizenship_status && (
              <IntakeFollowUp
                sub
                question={FOLLOW_UP_TEXT.spouse_citizenship_status.question}
                options={CITIZENSHIP_STATUS_OPTIONS}
                fact={facts.spouse_citizenship_status}
                onPick={set('spouse_citizenship_status')}
                notSure
              />
            )}
            {applies.spouse_eu_citizen && (
              <IntakeFollowUp
                sub
                question={FOLLOW_UP_TEXT.spouse_eu_citizen.question}
                hint={FOLLOW_UP_TEXT.spouse_eu_citizen.hint}
                options={EU_FOLLOW_UP_OPTIONS}
                fact={facts.spouse_eu_citizen}
                onPick={set('spouse_eu_citizen')}
                notSure
              />
            )}
          </div>
        )

      case '1.6':
        return (
          <div className="u-stack-sm">
            <IntakeFollowUp options={AGE_BAND_OPTIONS} fact={facts.age_band} onPick={set('age_band')} />
            {applies.spouse_age_band && (
              <IntakeFollowUp
                sub
                question={FOLLOW_UP_TEXT.spouse_age_band.question}
                options={AGE_BAND_OPTIONS}
                fact={facts.spouse_age_band}
                onPick={set('spouse_age_band')}
              />
            )}
          </div>
        )

      case '1.7': {
        const problem = countsProblem(facts)
        return (
          <div className="u-stack-sm">
            {COUNT_ROWS.map((row) => {
              const total = row.whole ? facts[row.whole] : null
              if (row.whole && !(total && total.status === 'known_value')) return null
              return (
                <IntakeCountField
                  key={row.key}
                  id={`intake-${row.key}`}
                  label={row.label}
                  fact={facts[row.key]}
                  max={row.whole ? total.value : undefined}
                  onChange={(fact) => (fact ? onFact(row.key, fact) : onClearFact(row.key))}
                />
              )
            })}
            <IntakeFollowUp question={DISABILITY_LABEL} options={YES_NO} fact={facts.disability_taxpayer} onPick={set('disability_taxpayer')} />
            {problem && <p className="triage-submit-error">A smaller group cannot be larger than the total. Please check the numbers.</p>}
          </div>
        )
      }

      case '5.1':
        return <IntakeFilingHistory filing={answers.filing} onChange={onFiling} />

      default:
        return null
    }
  }

  return (
    <div>
      <h1 className="triage-question">{cfg.title}</h1>
      {cfg.hint && <p className="triage-hint">{cfg.hint}</p>}
      {notice && <p className="intake-notice">{notice}</p>}
      {renderBody()}
      <div className="intake-actions">
        <button type="button" className="triage-next" onClick={onNext} disabled={!canNext || busy}>
          {nextLabel}
        </button>
      </div>
    </div>
  )
}
